import { Section } from '@advanza/advanza_generic'
import { call } from '@advanza/api'
import {
    PROFILE_STATUS_CHURNED,
    PROFILE_STATUS_DISABLED,
    PROFILE_STATUS_DISABLED_BAD_PROFILE,
    PROFILE_STATUS_ENABLED,
    PROFILE_STATUS_GDPR,
    PROFILE_STATUS_GHOST,
    PROFILE_STATUS_NEW_IMPORT,
    PROFILE_STATUS_ON_HOLD,
    PROFILE_STATUS_UNTOUCHED,
    WEBSITE_REGEX,
} from '@advanza/constants'
import { countrySettings } from '@advanza/func'
import { Col, Row } from '@advanza/grid'
import { ActionModal, SimpleModal } from '@advanza/modal'
import { changeEntity } from '@advanza/redux_entity'
import { Button, Divider, HoverText, Icon, LoadingDots, PreIcon } from '@advanza/ui'
import FixedWarningPopup from '@trustoo/components/ui/FixedWarningPopup'
import { CheckCircle } from '@untitledui/icons'
import AutoRow from 'components/misc/AutoRow'
import ProviderCityInput from 'components/providers/ProviderCityInput'
import ProvidersBanRestoreButton from 'components/providersBans/ProvidersBanRestoreButton'
import { getDate, isFromDatabase } from 'date'
import { format, isPast, isValid } from 'date-fns'
import { hasAccess } from 'misc/user'
import { saveProvider } from '../../actions/providers'
import EntityComponentContainer from '../../containers/EntityComponentContainer'
import EntityComponent from '../services/EntityComponent'
import PhoneRouting from './PhoneRouting'
import moduleStyle from './providerBusinessInfoEntity.module.css'

class ProviderBusinessInfoEntity extends EntityComponent {
    addCtlEnterSave = {
        onKeyDown: (event) => event.ctrlKey && event.key === 'Enter' && this.save(),
    }

    constructor(props) {
        super(props)
        this.state = {
            allowPhoneRoutingEdit: false,
            gdprModalOpen: false,
            gdprOverrideAcknowledged: false,
            quarantineModalOpen: false,
            statusBeforeGdpr: undefined,
            gdprConfirmSource: null,
            popupSaveSuccess: false,
            popupSaveError: false,
        }
        this.onQuarantine = this.onQuarantine.bind(this)
        this.closeAndRevertGdpr = this.closeAndRevertGdpr.bind(this)
        this.confirmGdpr = this.confirmGdpr.bind(this)
        this.permissions = window._user && window._user.permissions
    }

    onChangeAndSave = (e) => {
        const { name, value } = e.target
        if (name === 'status') {
            const newStatus = parseInt(value, 10)
            const currentStatus = this.props.entity.status
            if (newStatus === PROFILE_STATUS_GDPR && currentStatus !== PROFILE_STATUS_GDPR) {
                this.setState({
                    statusBeforeGdpr: currentStatus,
                    gdprModalOpen: true,
                    gdprConfirmSource: 'dropdown',
                })
                this.onChange(e)
                return
            }
        }
        this.onChange(e)
        this.save()
    }

    closeAndRevertGdpr() {
        const { statusBeforeGdpr } = this.state
        if (statusBeforeGdpr !== undefined) {
            this.onChangeEntity({ status: statusBeforeGdpr })
        }
        this.setState({
            gdprModalOpen: false,
            gdprOverrideAcknowledged: false,
            statusBeforeGdpr: undefined,
            gdprConfirmSource: null,
        })
    }

    confirmGdpr() {
        const { gdprConfirmSource, gdprOverrideAcknowledged } = this.state
        this.setState({ statusBeforeGdpr: undefined })

        if (gdprConfirmSource === 'button') {
            const { entityId, entity } = this.props
            const isAdmin = hasAccess('admin', 'w')
            const hasRecentOpenInvoice = !!entity.has_open_invoice_younger_than_60_days
            const payload =
                hasRecentOpenInvoice && isAdmin && gdprOverrideAcknowledged
                    ? { gdpr_open_invoice_override: true }
                    : {}
            return call(`office/providers/do-gdpr/${entityId}`, { payload })
                .catch((error) => {
                    this.setState({
                        popupSaveError: Date.now(),
                        popupSaveSuccess: false,
                        saveError: error,
                    })
                })
                .finally(() => window.location.reload())
        }

        return this.save({}, false).finally(() => window.location.reload())
    }

    togglePhoneRouteEdit() {
        const toggled = !this.state.allowPhoneRoutingEdit
        this.setState({ allowPhoneRoutingEdit: toggled })
        this.onChangeEntity({ _allowPhoneRoutingEdit: toggled })
    }

    onQuarantine() {
        const { entityId, storeBanEntity, invalidateProvidersBans } = this.props
        return call(`office/providers-bans/add-provider-to-bans/${entityId}`).then(({ ban }) => {
            this.onChangeEntity({
                bans: [ban.ban_id],
                status: 3,
            })
            storeBanEntity(ban)
            invalidateProvidersBans()
        })
    }

    editFields() {
        const entity = this.getEntity()
        const isAdmin = hasAccess('admin', 'w')
        const canGdpr = hasAccess('gdpr', 'w')
        const hasRecentOpenInvoice = !!entity.has_open_invoice_younger_than_60_days
        const blockGdprForNonAdmin = hasRecentOpenInvoice && !isAdmin
        // The status dropdown itself is `admin.w`-only — see render(). Within the
        // dropdown, the GDPR option additionally requires `gdpr.w` (and passes the
        // open-invoice chargeback guard). If the provider is already on GDPR, the
        // option stays enabled / full-strength so the current status icon is still
        // visible in the selected display; re-picking GDPR while already on GDPR is
        // a no-op.
        const isCurrentlyGdpr = entity.status === PROFILE_STATUS_GDPR
        const disableGdprOption = !isCurrentlyGdpr && (!canGdpr || blockGdprForNonAdmin)
        const gdprDisabledReason = canGdpr
            ? 'Provider has an open invoice from less than 60 days ago'
            : "You don't have permission to set GDPR status"
        const gdprFullIcon = (
            <Icon
                title={disableGdprOption ? gdprDisabledReason : 'gdpr'}
                key={'delete_forever'}
                style={{ color: 'purple' }}
                name="delete_forever"
            />
        )
        const gdprOptionTitle = disableGdprOption ? (
            <span style={{ opacity: 0.4, cursor: 'not-allowed' }}>{gdprFullIcon}</span>
        ) : (
            gdprFullIcon
        )
        const fullStatusOptions = [
            {
                title: <Icon key={'power'} title="enabled" green name="power" />,
                value: PROFILE_STATUS_ENABLED,
            },
            {
                title: <Icon key={'power_off'} title="disabled" red name="power_off" />,
                value: PROFILE_STATUS_DISABLED,
            },
            {
                title: (
                    <Icon key={'thumb_down_alt'} title="bad profile" red name="thumb_down_alt" />
                ),
                value: PROFILE_STATUS_DISABLED_BAD_PROFILE,
            },
            {
                title: <Icon key={'pause_circle'} title="on hold" red name="pause_circle" />,
                value: PROFILE_STATUS_ON_HOLD,
            },
            {
                title: <Icon key={'pending'} title="untouched" orange name="pending" />,
                value: PROFILE_STATUS_UNTOUCHED,
            },
            {
                title: gdprOptionTitle,
                value: PROFILE_STATUS_GDPR,
                disabled: disableGdprOption,
            },
            {
                title: <Icon key={'ghost'} title="ghost" blue name="👻" />,
                value: PROFILE_STATUS_GHOST,
            },
            {
                title: <Icon key={'fiber_new'} title="new import" orange name="fiber_new" />,
                value: PROFILE_STATUS_NEW_IMPORT,
            },
            {
                title: <Icon key={'stop_circle'} title="churned" red name="stop_circle" />,
                value: PROFILE_STATUS_CHURNED,
            },
        ]
        const statusOptions =
            entity.status === PROFILE_STATUS_GHOST && !hasAccess('sales')
                ? [{ title: <Icon key={'ghost'} blue name="👻" />, value: PROFILE_STATUS_GHOST }]
                : fullStatusOptions
        return {
            business_name: {
                type: 'geo',
                msg: 'Business name',
                map: {
                    street_number: 'street_number',
                    postal_code: 'postalcode',
                    route: 'street',
                    formatted_phone_number: 'phone',
                    website: 'website',
                    name: 'business_name',
                },
                validator: this.textValidator,
                ...this.addCtlEnterSave,
            },
            profile_url: {
                type: 'string',
                placeholder: 'Profile url',
                validator: (value) => {
                    return (
                        /*
                          This regex will match:
                        - Lowercase letters
                        - Numbers
                        - A range of UTF-8 characters
                        - Underscore
                        - Dash
                        * */
                        value && value.match(/^[a-z0-9_\-\p{Ll}]+$/gu)
                    )
                },
                defaultErrorMsg:
                    'profile url is required and can only contain letters, numbers and -',
            },
            phone: { type: 'phone', msg: 'Phone', ...this.addCtlEnterSave },
            secondary_phone: { type: 'phone', msg: 'Secondary phone', ...this.addCtlEnterSave },
            phone_routing_nr: {
                type: 'phone',
                msg: 'Phone routing nr',
                readOnly: !entity._allowPhoneRoutingEdit,
            },
            use_phone_routing: { type: 'boolean', msg: 'use phone routing' },
            has_gmb: { type: 'boolean', msg: 'GMB claimed' },
            has_facebook: { type: 'boolean', msg: 'Has Facebook' },
            show_expert_blogs: { type: 'boolean', msg: 'Show expert blogs' },

            use_active_campaign: { type: 'boolean', msg: 'Use HubSpot' },
            status: {
                type: 'select',
                className: 'select-min select-stealth',
                options: statusOptions,
                ...this.addCtlEnterSave,
            },
            email: { type: 'email', msg: 'Email', ...this.addCtlEnterSave },
            street_number: { type: 'text', msg: 'street nr', ...this.addCtlEnterSave },
            street_number_extra: { type: 'text', msg: 'street nr extra', ...this.addCtlEnterSave },
            street: { type: 'text', msg: 'Street', ...this.addCtlEnterSave },
            postalcode: { type: 'text', msg: 'Postal code', ...this.addCtlEnterSave },
            gmb_url: { type: 'text', msg: 'GMB url', ...this.addCtlEnterSave },
            facebook_url: { type: 'text', msg: 'Facebook url', ...this.addCtlEnterSave },
            linkedin_url: { type: 'text', msg: 'LinkedIn url', ...this.addCtlEnterSave },
            ...(entity.country_code.toUpperCase() === 'DE'
                ? {
                      xing_url: {
                          type: 'text',
                          msg: 'Xing url',
                          ...this.addCtlEnterSave,
                          validator: (val) =>
                              !val || val.indexOf('https://www.xing.com/pages/') === 0,
                          defaultErrorMsg: 'Has to start with https://www.xing.com/pages/',
                      },
                  }
                : {}),
            nr_employees: {
                type: 'number',
                msg: 'Nr. employees',
                ...this.addCtlEnterSave,
            },
            kvk: { type: 'text', msg: 'kvk', ...this.addCtlEnterSave },
            line_creative: {
                msg: 'Line creative',
                validator: (val) => !val || val.length <= 200,
                ...this.addCtlEnterSave,
            },
            established: {
                type: 'text',
                msg: 'Established on:',
                inputPlaceholder: 'yyyy-mm-dd',
                ...this.addCtlEnterSave,
                validator: (val) =>
                    !val || val.length === 0 || (isValid(new Date(val)) && isPast(new Date(val))),
            },
            claimed: {
                type: 'text',
                msg: 'Claimed on:',
                inputPlaceholder: 'yyyy-mm-dd hh:mm:ss',
                ...this.addCtlEnterSave,
                validator: (val) =>
                    !val || val.length === 0 || (isValid(new Date(val)) && isPast(new Date(val))),
                valueFormatter: (val) =>
                    isFromDatabase(val) ? format(getDate(val), 'yyyy-MM-dd HH:mm:ss') : val,
            },
            business_keywords: { type: 'text', msg: 'keyword list', ...this.addCtlEnterSave },
            years_experience: {
                type: 'number',
                msg: 'Years experience',
                ...this.addCtlEnterSave,
            },
            country_code: {
                type: 'select',
                className: 'select-min',
                placeholder: 'Country',
                options: [
                    { value: 'NL', title: 'NL' },
                    { value: 'BE', title: 'BE' },
                ],
            },
            website: {
                type: 'url',
                msg: 'Website url',
                ...this.addCtlEnterSave,
                validator: (val) => !val || val.match(WEBSITE_REGEX),
            },
            description: {
                type: 'textArea',
                msgid: 'Description',
                ...this.addCtlEnterSave,
            },
            external_id: {
                placeholder: 'Advanza id',
                style: { maxWidth: 140 },
                ...this.addCtlEnterSave,
            },
            unlinked_from_advanza: { type: 'date', msg: 'Unlink from advanza' },
            hide_place: { type: 'boolean', msg: 'Hide place' },
        }
    }

    save(params, doCheck = true) {
        const { entity } = this.props

        if (
            doCheck &&
            entity._beforeSave &&
            entity._beforeSave.status !== 4 &&
            entity.status === 4
        ) {
            this.setState({ gdprModalOpen: true })
            return Promise.resolve()
        }

        const isAdmin = hasAccess('admin', 'w')
        const hasRecentOpenInvoice = !!entity.has_open_invoice_younger_than_60_days
        const isGdpring = entity.status === 4 && entity._beforeSave?.status !== 4
        const overrideAck = this.state.gdprOverrideAcknowledged
        const finalParams =
            isGdpring && hasRecentOpenInvoice && isAdmin && overrideAck
                ? { ...(params || {}), gdpr_open_invoice_override: true }
                : params

        return super.save(finalParams).then(
            () => {
                this.setState({
                    popupSaveSuccess: Date.now(),
                    popupSaveError: false,
                    gdprOverrideAcknowledged: false,
                })
            },
            (response) => {
                this.setState({
                    popupSaveError: Date.now(),
                    popupSaveSuccess: false,
                    saveError: response,
                })
            },
        )
    }

    render() {
        const { entityId, entity } = this.props
        const {
            allowPhoneRoutingEdit,
            gdprModalOpen,
            gdprOverrideAcknowledged,
            quarantineModalOpen,
        } = this.state
        const href =
            entity.website?.indexOf('//') === -1 ? `http://${entity.website}` : entity.website
        const isAdmin = hasAccess('admin', 'w')
        const hasRecentOpenInvoice = !!entity.has_open_invoice_younger_than_60_days
        const gdprBlockedForUser = hasRecentOpenInvoice && !isAdmin
        const gdprNeedsAdminOverride = hasRecentOpenInvoice && isAdmin
        const gdprModalConfirmDisabled = gdprNeedsAdminOverride && !gdprOverrideAcknowledged
        const gdprModalBody = gdprNeedsAdminOverride ? (
            <div>
                <p>
                    This account will be deleted. Only company name, kvk, and address will be kept,
                    plus billing and contact details while the provider has an open invoice. This
                    can take a few minutes to process, the profile and status may not update right
                    away.
                </p>
                <p
                    style={{
                        color: '#b00020',
                        fontWeight: 'bold',
                        marginTop: '0.5rem',
                        marginBottom: '1rem',
                    }}>
                    Warning: this provider has an open invoice from less than 60 days ago. GDPR'ing
                    them now may enable chargebacks.
                </p>
                <label style={{ cursor: 'pointer', userSelect: 'none', display: 'block' }}>
                    <input
                        type="checkbox"
                        style={{ display: 'none' }}
                        checked={gdprOverrideAcknowledged}
                        onChange={(e) =>
                            this.setState({ gdprOverrideAcknowledged: e.target.checked })
                        }
                    />
                    <PreIcon
                        top
                        overflowFix
                        primColor={gdprOverrideAcknowledged}
                        icon={gdprOverrideAcknowledged ? 'check_box' : 'check_box_outline_blank'}>
                        I acknowledge there is an open invoice from less than 60 days ago.
                    </PreIcon>
                </label>
            </div>
        ) : (
            'This account will be deleted. Only company name, kvk, and address will be kept, plus billing and contact details while the provider has an open invoice. This can take a few minutes to process, the profile and status may not update right away.'
        )
        return (
            <div>
                <SimpleModal
                    header="Delete account - Are you sure?"
                    body={gdprModalBody}
                    buttons={[
                        {
                            text: "No, don't delete",
                            name: 'bordered',
                            func: this.closeAndRevertGdpr,
                        },
                        {
                            text: 'Yes, delete account',
                            disabled: gdprModalConfirmDisabled,
                            func: this.confirmGdpr,
                        },
                    ]}
                    cancelFunc={this.closeAndRevertGdpr}
                    isOpen={gdprModalOpen}
                    setIsOpen={(open) =>
                        open ? this.setState({ gdprModalOpen: true }) : this.closeAndRevertGdpr()
                    }
                    isSaving={entity._saving}
                />
                <FixedWarningPopup timeout={null} icon={false} fixed show={Boolean(entity._saving)}>
                    <div className={'flex gap-1 items-center'}>
                        {entity._saving && <LoadingDots color="#fff" />}
                        <div>Saving business info</div>
                    </div>
                </FixedWarningPopup>
                <FixedWarningPopup
                    key={this.state.popupSaveSuccess}
                    green
                    icon={<CheckCircle />}
                    fixed
                    show={Boolean(this.state.popupSaveSuccess)}
                    onExited={() => this.setState({ popupSaveSuccess: false })}>
                    Saved !
                </FixedWarningPopup>
                <FixedWarningPopup
                    key={this.state.popupSaveError}
                    timeout={5000}
                    red
                    fixed
                    show={!entity._saving && Boolean(this.state.popupSaveError)}
                    onExited={() => this.setState({ popupSaveError: false })}>
                    <pre>{JSON.stringify(this.state.saveError, null, 2)}</pre>
                </FixedWarningPopup>
                <Row middle="xs" between="xs">
                    <Col x>
                        <Divider />
                        {this.renderInput('external_id')}
                    </Col>
                    <Col x>
                        <Button disabled={!entity._isTouched} onClick={() => this.save()}>
                            <PreIcon icon="save" center>
                                {entity._saving ? <LoadingDots color="#fff" /> : 'Save'}
                            </PreIcon>
                        </Button>
                    </Col>
                </Row>
                <Divider m />
                {this.renderInput('business_name')}
                <Divider m />
                {this.renderInput('profile_url')}
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('kvk')}</Col>
                    <Col x>
                        <a
                            target="_blank"
                            href={`https://www.graydongo.nl/zoekresultaten?q=${entity.business_name}`}>
                            <Icon name="search" />
                        </a>
                    </Col>
                    <Col x>{this.renderInput('claimed')}</Col>
                </Row>
                <Divider m />
                <Row between="xs">
                    <Col x>
                        {(entity.bans || []).length ? (
                            <ProvidersBanRestoreButton providerId={entityId} />
                        ) : (
                            <Button onClick={() => this.setState({ quarantineModalOpen: true })}>
                                <Icon name="coronavirus" /> Quarantine provider
                            </Button>
                        )}

                        {this.permissions.gdpr?.w &&
                            (gdprBlockedForUser ? (
                                <HoverText
                                    enableClick
                                    trigger={
                                        <Button
                                            disabled
                                            style={{ marginLeft: '10px', cursor: 'help' }}>
                                            <Icon name="gpp_bad" /> GDPR Disable
                                        </Button>
                                    }>
                                    Provider has an open invoice from less than 60 days ago.
                                </HoverText>
                            ) : (
                                <Button
                                    style={{ marginLeft: '10px' }}
                                    onClick={() => {
                                        this.setState({
                                            gdprModalOpen: true,
                                            gdprConfirmSource: 'button',
                                        })
                                    }}>
                                    <Icon name="gpp_bad" /> GDPR Disable
                                </Button>
                            ))}

                        <ActionModal
                            cancelText="No"
                            text="Yes"
                            func={this.onQuarantine}
                            header="Quarantine provider, are you sure?"
                            isOpen={quarantineModalOpen}
                            setIsOpen={(quarantineModalOpen) =>
                                this.setState({ quarantineModalOpen })
                            }
                        />
                    </Col>

                    <Col x>
                        {entity.franchise_status ? (
                            <HoverText
                                trigger={
                                    <Icon
                                        name="layers"
                                        style={{ color: 'rgb(100,100,100)', marginTop: 12 }}
                                    />
                                }>
                                This account represents a whole franchise, not a single provider.
                                Its profile is never published.
                            </HoverText>
                        ) : (
                            <div className="flex items-center gap-2">
                                <small>Status:</small>
                                {hasAccess('admin', 'w') ? (
                                    this.renderInput('status', true)
                                ) : (
                                    <HoverText
                                        enableClick
                                        trigger={
                                            <span style={{ cursor: 'help', opacity: 0.85 }}>
                                                {this.getFields().status.options.find(
                                                    (option) => option.value === entity.status,
                                                )?.title || null}
                                            </span>
                                        }>
                                        Only admins can change a provider's status from the
                                        dropdown.
                                    </HoverText>
                                )}
                            </div>
                        )}
                    </Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('phone')}</Col>
                    <Col xs>{this.renderInput('secondary_phone')}</Col>
                </Row>
                {countrySettings[entity.country_code].useQooqie && (
                    <>
                        <Divider m />
                        <Row className="items-baseline">
                            <button onClick={() => this.togglePhoneRouteEdit()}>
                                <Icon name={allowPhoneRoutingEdit ? 'lock_open' : 'lock'} />
                            </button>
                            <Col xs>
                                <div id="phone_routing_nr">
                                    <span
                                        className={allowPhoneRoutingEdit ? '' : moduleStyle.noEdit}>
                                        {this.renderInput('phone_routing_nr')}
                                    </span>
                                    <Divider />
                                    <AutoRow>
                                        <PhoneRouting
                                            change={this.onChangeEntity}
                                            provider={entity}
                                        />
                                        {['DELETED', 'PENDING', 'REQUESTED'].filter((sub) =>
                                            entity.phone_routing_nr?.includes(sub),
                                        ).length === 0 && this.renderInput('use_phone_routing')}
                                    </AutoRow>
                                </div>
                            </Col>
                        </Row>
                    </>
                )}
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('email')}</Col>
                    <Col x>{this.renderInput('website')}</Col>
                    <Col x>
                        <a href={href} target="_blank">
                            <Icon>link</Icon>
                        </a>
                    </Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('nr_employees')}</Col>
                    <Col xs>{this.renderInput('established')}</Col>
                    <Col xs>{this.renderInput('years_experience')}</Col>
                </Row>
                <Divider m />
                {this.renderInput('business_keywords')}
                <Divider m />
                <Row middle="xs">
                    <Col xs={6}>{this.renderInput('street')}</Col>
                    <Col xs={6}>{this.renderInput('street_number')}</Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs={6}>{this.renderInput('street_number_extra')}</Col>
                    <Col xs={6}>{this.renderInput('hide_place')}</Col>
                </Row>
                <Divider m />
                <Row middle="xs" style={{ flexWrap: 'nowrap' }}>
                    <Col x>{this.renderInput('postalcode')} </Col>
                    <Col x>
                        <ProviderCityInput
                            className="w-[200px]"
                            postalCode={entity.postalcode}
                            countryCode={entity.country_code}
                            value={entity && entity.cityName}
                            onChangeLocation={(place) => {
                                this.onChangeEntity({
                                    postal_code_id: place.postal_code_id,
                                    cityName: place.place_name,
                                    postalcode: place.postal_code,
                                })
                            }}
                        />
                    </Col>
                    <Col x>
                        <Section w>{this.renderInput('country_code')}</Section>{' '}
                    </Col>
                </Row>
                {this.renderInput('description')}
                <Divider m />
                {this.renderInput('line_creative')}
                <small style={{ color: entity.line_creative.length > 200 && 'red' }}>
                    {entity.line_creative.length}/200
                </small>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('has_gmb')}</Col>
                    <Col xs>{this.renderInput('has_facebook')}</Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('gmb_url')}</Col>
                    <Col xs>{this.renderInput('facebook_url')}</Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('linkedin_url')}</Col>
                    <Col xs>
                        {entity.country_code.toUpperCase() === 'DE' && this.renderInput('xing_url')}
                    </Col>
                </Row>
                <Divider m />
                <Row middle="xs">
                    <Col xs>{this.renderInput('use_active_campaign')}</Col>
                    <Col xs>{this.renderInput('show_expert_blogs')}</Col>
                </Row>
                <Divider m />
                <Row end="xs">
                    <Col x>
                        <Button disabled={!entity._isTouched} onClick={() => this.save()}>
                            {entity._saving ? 'saving..' : 'Save'}
                        </Button>
                    </Col>
                </Row>
            </div>
        )
    }
}

export default EntityComponentContainer(ProviderBusinessInfoEntity, {
    store: 'providers',
    name: 'providers',
    saveFunc: saveProvider,
    mapDispatchToProps: (dispatch) => ({
        storeBanEntity: (ban) =>
            dispatch(
                changeEntity({
                    store: 'providers',
                    name: 'bans',
                    key: ban.ban_id,
                    diff: ban,
                }),
            ),
        invalidateProvidersBans: () => dispatch({ type: 'INVALIDATE_PROVIDERSBANS' }),
    }),
})
